import React, { useEffect, useState } from "react";
import Box from "@mui/joy/Box";
import Typography from "@mui/joy/Typography";
import FormControl from "@mui/joy/FormControl";
import FormLabel from "@mui/joy/FormLabel";
import Input from "@mui/joy/Input";
import Button from "@mui/joy/Button";
import Table from "@mui/joy/Table";
import CircularProgress from "@mui/joy/CircularProgress";
import Link from "@mui/joy/Link";
import Chip from "@mui/joy/Chip";
import IconButton from "@mui/joy/IconButton";
import Tooltip from "@mui/joy/Tooltip";
import { useNavigate } from "react-router-dom";
import { ProfitAdjustmentsApi } from "./profitAdjustmentsApi";
import { ProfitAdjustmentDto, ProfitAdjustmentListResult } from "../../../models/profitAdjustments";
import { RequestResultValue } from "../../../common/api/requestResult";
import { formatRubles } from "../../../common/money";
import { formatDateTime } from "../../../common/dateTime";
import { NotificationService } from "../../../common/notification";
import EditIcon from "@mui/icons-material/Edit";
import { ProfitAdjustmentEditor } from "./ProfitAdjustmentEditor";

export function ProfitAdjustmentsTab() {
    const navigate = useNavigate();

    const [userId, setUserId] = useState<string>("");
    const [page, setPage] = useState<number>(1);
    const count = 30;
    const [loading, setLoading] = useState<boolean>(false);
    const [result, setResult] = useState<ProfitAdjustmentListResult | null>(null);
    const [editorOpen, setEditorOpen] = useState(false);
    const [editorMode, setEditorMode] = useState<"create" | "edit">("edit");
    const [editingAdjustment, setEditingAdjustment] = useState<ProfitAdjustmentDto | null>(null);

    const load = async () => {
        setLoading(true);
        try {
            const res: RequestResultValue<ProfitAdjustmentListResult> = await ProfitAdjustmentsApi.list({
                page,
                count,
                userId: userId || undefined,
            });
            if (res.result && res.value) {
                setResult(res.value);
            } else {
                NotificationService.alertError("Пожалуйста попробуйте позже или обратитесь в службу поддержки");
                setResult({
                    total: 0,
                    items: [],
                    count,
                });
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load().catch(console.error);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page]);

    const clearFilters = async () => {
        setUserId("");
        setPage(1);
        try {
            const res = await ProfitAdjustmentsApi.list({ page: 1, count, userId: undefined });
            if (res.result && res.value) {
                setResult(res.value);
            } else {
                NotificationService.alertError("Пожалуйста попробуйте позже или обратитесь в службу поддержки");
                setResult({
                    total: 0,
                    items: [],
                    count,
                });
            }
        } catch (e) {
            console.error(e);
            NotificationService.alertError("Пожалуйста попробуйте позже или обратитесь в службу поддержки");
        }
    };

    const total = result?.total ?? 0;
    const totalPages = Math.max(1, Math.ceil(total / count));


    return (
        <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography level="h3" component="h2" sx={{ mb: 1 }}>
                Корректировки прибыли
            </Typography>

            {/* Фильтры */}
            <Box
                sx={{
                    p: 2,
                    bgcolor: "background.level1",
                    borderRadius: "8px",
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 2,
                    alignItems: "flex-end",
                }}
            >
                <FormControl size="sm" sx={{ minWidth: { xs: "100%", sm: 200 }, flex: { xs: "1 1 100%", sm: "0 0 auto" } }}>
                    <FormLabel>Юзер ID</FormLabel>
                    <Input
                        fullWidth
                        value={userId}
                        onChange={(e) => setUserId(e.target.value)}
                        placeholder="12345"
                    />
                </FormControl>

                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
                    <Button size="sm" onClick={() => { setPage(1); load().catch(console.error); }}>
                        Применить
                    </Button>
                    <Button size="sm" variant="outlined" onClick={clearFilters}>
                        Сбросить
                    </Button>
                    <Button
                        size="sm"
                        color="primary"
                        onClick={() => {
                            setEditorMode("create");
                            setEditingAdjustment(null);
                            setEditorOpen(true);
                        }}
                    >
                        Добавить
                    </Button>
                </Box>
            </Box>

            {/* Десктопная таблица */}
            <Box sx={{ position: "relative", display: { xs: "none", md: "block" } }}>
                {loading && (
                    <Box
                        sx={{
                            position: "absolute",
                            inset: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            bgcolor: "background.body",
                            opacity: 0.6,
                            zIndex: 1,
                        }}
                    >
                        <CircularProgress />
                    </Box>
                )}

                <Box sx={{ overflowX: "auto" }}>
                    <Table
                        hoverRow
                        sx={{
                            "& th": { whiteSpace: "nowrap" },
                            tableLayout: "fixed",
                            minWidth: 900,
                        }}
                    >
                        <colgroup>
                            <col style={{ width: 40 }} />
                            <col style={{ width: 140 }} />
                            <col style={{ width: 100 }} />
                            <col />
                            <col style={{ width: 170 }} />
                            <col style={{ width: 170 }} />
                            <col style={{ width: 50 }} />
                        </colgroup>
                        <thead>
                            <tr>
                                <th>ID</th>
                                <th>Пользователь</th>
                                <th>Сумма</th>
                                <th>Комментарий</th>
                                <th>Дата корректировки</th>
                                <th>Создано / кем</th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {(result?.items ?? []).map((row: ProfitAdjustmentDto) => (
                                <tr key={String(row.id)}>
                                    <td>{String(row.id)}</td>
                                    <td>
                                        {row.userId ? (
                                            <Link
                                                underline="hover"
                                                onClick={() => navigate(`/app/users/${String(row.userId)}`)}
                                                sx={{ cursor: "pointer" }}
                                            >
                                                {String(row.userId)}
                                            </Link>
                                        ) : (
                                            "-"
                                        )}
                                    </td>
                                    <td>{formatRubles(row.amount)}</td>
                                    <td style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                        {row.comment ? (
                                            <Tooltip title={row.comment}>
                                                <span>{row.comment}</span>
                                            </Tooltip>
                                        ) : (
                                            "-"
                                        )}
                                    </td>
                                    <td>{formatDateTime(row.date)}</td>
                                    <td>
                                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                                            <span>{formatDateTime(row.created)}</span>
                                            {row.createdBy != null ? (
                                                <Link
                                                    underline="hover"
                                                    onClick={() => navigate(`/app/users/${String(row.createdBy)}`)}
                                                    sx={{
                                                        cursor: "pointer",
                                                        fontSize: "0.8rem",
                                                        color: "var(--joy-palette-text-tertiary)",
                                                    }}
                                                >
                                                    {String(row.createdBy)}
                                                </Link>
                                            ) : (
                                                <span
                                                    style={{
                                                        fontSize: "0.8rem",
                                                        color: "var(--joy-palette-text-tertiary)",
                                                    }}
                                                >
                                                    -
                                                </span>
                                            )}
                                        </Box>
                                    </td>
                                    <td>
                                        <Tooltip title="Редактировать">
                                            <IconButton
                                                size="sm"
                                                variant="outlined"
                                                onClick={() => {
                                                    setEditorMode("edit");
                                                    setEditingAdjustment(row);
                                                    setEditorOpen(true);
                                                }}
                                            >
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </td>
                                </tr>
                            ))}

                            {!loading && (result?.items?.length ?? 0) === 0 && (
                                <tr>
                                    <td colSpan={6}>
                                        <Typography level="body-sm" sx={{ textAlign: "center", py: 2 }}>
                                            Нет данных
                                        </Typography>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </Box>
            </Box>

            {/* Мобильная версия */}
            <Box sx={{ display: { xs: "grid", md: "none" }, gap: 1 }}>
                {loading && (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                        <CircularProgress size="sm" />
                    </Box>
                )}

                {!loading && (result?.items?.length ?? 0) === 0 && (
                    <Typography level="body-sm" sx={{ textAlign: "center", py: 2 }}>
                        Нет данных
                    </Typography>
                )}

                {(result?.items ?? []).map((row: ProfitAdjustmentDto) => (
                    <Box
                        key={String(row.id)}
                        sx={{
                            p: 1.5,
                            borderRadius: "8px",
                            border: "1px solid",
                            borderColor: "divider",
                            bgcolor: "background.level1",
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.75,
                        }}
                    >
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
                            <Typography level="title-sm">#{String(row.id)}</Typography>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                <Typography level="body-xs">{formatDateTime(row.date)}</Typography>
                                <Tooltip title="Редактировать">
                                    <IconButton
                                        size="sm"
                                        variant="outlined"
                                        onClick={() => {
                                            setEditorMode("edit");
                                            setEditingAdjustment(row);
                                            setEditorOpen(true);
                                        }}
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>

                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
                            <Chip size="sm" variant="soft">
                                Сумма: {formatRubles(row.amount)}
                            </Chip>
                            {row.userId && (
                                <Chip size="sm" variant="soft">
                                    Пользователь:&nbsp;
                                    <Link
                                        underline="hover"
                                        onClick={() => navigate(`/app/users/${String(row.userId)}`)}
                                        sx={{ cursor: "pointer" }}
                                    >
                                        {String(row.userId)}
                                    </Link>
                                </Chip>
                            )}
                            <Chip size="sm" variant="soft">
                                Дата:&nbsp;{formatDateTime(row.created)}
                            </Chip>
                            {row.createdBy != null && (
                                <Chip size="sm" variant="soft">
                                    Кем:&nbsp;
                                    <Link
                                        underline="hover"
                                        onClick={() => navigate(`/app/users/${String(row.createdBy)}`)}
                                        sx={{ cursor: "pointer" }}
                                    >
                                        {String(row.createdBy)}
                                    </Link>
                                </Chip>
                            )}
                            {row.comment && (
                                <Tooltip title={row.comment}>
                                    <Chip
                                        size="sm"
                                        variant="outlined"
                                        sx={{ maxWidth: "100%", whiteSpace: "normal", wordBreak: "break-word" }}
                                    >
                                        {row.comment}
                                    </Chip>
                                </Tooltip>
                            )}
                        </Box>
                    </Box>
                ))}
            </Box>

            {/* Пагинация */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mt: 1,
                    flexWrap: "wrap",
                    gap: 1,
                }}
            >
                <Typography level="body-sm">Всего: {total}</Typography>
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <Button
                        size="sm"
                        variant="outlined"
                        disabled={page <= 1}
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                        Назад
                    </Button>
                    <Typography level="body-sm">
                        {page} / {totalPages}
                    </Typography>
                    <Button
                        size="sm"
                        variant="outlined"
                        disabled={page >= totalPages}
                        onClick={() => setPage((p) => p + 1)}
                    >
                        Вперёд
                    </Button>
                </Box>
            </Box>

            <ProfitAdjustmentEditor
                open={editorOpen}
                mode={editorMode}
                adjustment={editingAdjustment}
                onClose={() => setEditorOpen(false)}
                onSaved={async () => {
                    await load();
                }}
            />
        </Box>
    );
}

